/**
 * Оплаты из банка и 1С (ФН-04): строки money-портала. Разнесённые видны с заявкой на оплату; очередь
 * неразнесённых — со подсказками: выбрать заявку, отнести на проект или снять с учёта с причиной.
 */
import { type MoneyPaymentView, seesMoney } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { date, tenge } from '../../lib/format';
import { projectsQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

type Resp = { unmatched: number; items: MoneyPaymentView[] };
const KEY = ['procurement', 'money-payments'];

function Row({ m }: { m: MoneyPaymentView }) {
  const qc = useQueryClient();
  const projects = useQuery({ ...projectsQuery('all'), enabled: m.status === 'unmatched' });
  const [mode, setMode] = useState<'' | 'request' | 'project' | 'ignore'>('');
  const [requestId, setRequestId] = useState(m.suggestions[0]?.id ?? '');
  const [projectId, setProjectId] = useState('');
  const [reason, setReason] = useState('');
  const resolve = useMutation({
    mutationFn: () =>
      api(`/procurement/money-payments/${m.id}/resolve`, {
        method: 'POST',
        body:
          mode === 'request'
            ? { action: 'request', paymentRequestId: requestId }
            : mode === 'project'
              ? { action: 'project', projectId }
              : { action: 'ignore', reason },
      }),
    onSuccess: () => {
      toast('Строка разнесена');
      setMode('');
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['procurement', 'payment-requests'] });
    },
  });
  const tone = m.status === 'matched' ? 'success' : m.status === 'unmatched' ? 'warning' : 'neutral';
  return (
    <li className="flex flex-col gap-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="min-w-0 font-medium [overflow-wrap:anywhere] text-ink">
          {m.direction === 'in' ? 'Поступление' : 'Оплата'} {m.amount !== null ? tenge(m.amount) : ''} ·{' '}
          {date(m.paidOn)}
        </span>
        <Badge tone={tone}>{m.statusTitle}</Badge>
      </div>
      <p className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
        {m.counterpartyName ?? 'Контрагент не указан'}
        {m.counterpartyBin ? ` · БИН ${m.counterpartyBin}` : ''} · {m.source === 'bank' ? 'банк' : '1С'}
        {m.accountLast4 ? ` · счёт …${m.accountLast4}` : ''}
        {m.docNumber ? ` · ${m.docNumber}` : ''}
      </p>
      {m.purpose ? <p className="text-[13px] [overflow-wrap:anywhere] text-ink-2">{m.purpose}</p> : null}
      {m.paymentRequestNumber ? (
        <p className="text-[13px] text-ink">
          Заявка на оплату {m.paymentRequestNumber}
          {m.matchedBy === 'auto' ? ' (разнесена порталом)' : ' (разнесена вручную)'}
        </p>
      ) : null}
      {m.reason ? <p className="text-[13px] [overflow-wrap:anywhere] text-ink-2">{m.reason}</p> : null}
      {m.status === 'unmatched' ? (
        <div className="flex flex-col gap-2 border-t border-line pt-2">
          <div className="flex flex-wrap gap-2">
            {m.direction === 'out' ? (
              <Button size="sm" onClick={() => setMode('request')}>
                На заявку на оплату
              </Button>
            ) : null}
            <Button size="sm" onClick={() => setMode('project')}>
              На проект
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setMode('ignore')}>
              Снять с учёта
            </Button>
          </div>
          {mode === 'request' ? (
            <Field label="Заявка на оплату" htmlFor={`rq-${m.id}`}>
              <Select
                className="h-11 md:h-10"
                id={`rq-${m.id}`}
                value={requestId}
                onChange={(e) => setRequestId(e.target.value)}
              >
                <option value="">Выберите заявку из подсказок</option>
                {m.suggestions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.number} · совпадение {s.score}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {mode === 'project' ? (
            <Field label="Проект" htmlFor={`pj-${m.id}`}>
              <Select
                className="h-11 md:h-10"
                id={`pj-${m.id}`}
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">Выберите проект</option>
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} — {p.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {mode === 'ignore' ? (
            <Field label="Причина" htmlFor={`ig-${m.id}`}>
              <Input
                className="h-11 md:h-10"
                id={`ig-${m.id}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          ) : null}
          {mode ? (
            <div>
              <Button
                size="sm"
                variant="primary"
                loading={resolve.isPending}
                disabled={
                  (mode === 'request' && !requestId) ||
                  (mode === 'project' && !projectId) ||
                  (mode === 'ignore' && reason.trim().length < 3)
                }
                onClick={() => resolve.mutate()}
              >
                Разнести
              </Button>
            </div>
          ) : null}
          {resolve.error ? <ErrorBox error={resolve.error} /> : null}
        </div>
      ) : null}
    </li>
  );
}

export function MoneyPaymentsView() {
  const me = useMe();
  const money = seesMoney(me.access, 'acts_invoices');
  const [status, setStatus] = useState<'unmatched' | 'matched' | 'ignored'>('unmatched');
  const q = useQuery({
    queryKey: [...KEY, status],
    queryFn: () => api<Resp>('/procurement/money-payments', { query: { status, limit: 100 } }),
    enabled: money,
  });
  if (!money)
    return (
      <EmptyState title="Оплаты из банка — с суммами">
        Раздел показывают сотрудникам с уровнем денег «Акты и счета» или выше и правом фиксировать факт
        оплаты.
      </EmptyState>
    );
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Оплаты из банка и 1С">
        <p className="mb-3 text-[13px] text-ink-2">
          Портал забирает платежи из money-портала раз в час и сам отмечает заявки на оплату, когда совпали
          сумма, БИН и назначение. Остальное ждёт вас здесь.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Показать" htmlFor="mp-status" className="min-w-[200px]">
            <Select
              className="h-11 md:h-10"
              id="mp-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
            >
              <option value="unmatched">Не разнесённые{q.data ? ` (${q.data.unmatched})` : ''}</option>
              <option value="matched">Разнесённые</option>
              <option value="ignored">Снятые с учёта</option>
            </Select>
          </Field>
        </div>
      </Panel>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : q.data.items.length === 0 ? (
        <EmptyState title="Строк нет">
          {status === 'unmatched'
            ? 'Неразнесённых оплат нет: всё сопоставлено или загрузка ещё не шла. Коннектор «Money-портал» настраивается в администрировании, «Обмен с системами».'
            : 'Строк с таким статусом нет.'}
        </EmptyState>
      ) : (
        <ul className="flex flex-col gap-3">
          {q.data.items.map((m) => (
            <Row key={m.id} m={m} />
          ))}
        </ul>
      )}
    </div>
  );
}
